import { useEffect, useState } from "react";
import { Download, X, Share, MoreVertical, Smartphone } from "lucide-react";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export default function InstallAppButton({ className = "" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* ignore */ }
      setDeferred(null);
    } else {
      setShowHelp(true);
    }
  };

  // Show on browsers that support the native prompt, or on iOS (manual add).
  if (!deferred && !isIos()) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="install-app-btn"
        className={className || "w-full inline-flex items-center justify-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] py-3 text-sm font-semibold hover:bg-[#D4AF37]/10 transition-colors"}
      >
        <Download size={16} /> Inštalovať aplikáciu na plochu
      </button>

      {showHelp && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" data-testid="install-help-modal" onClick={() => setShowHelp(false)}>
          <div className="w-full max-w-sm bg-[#0a0a0a] border border-[#D4AF37]/40 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Smartphone size={18} className="text-[#D4AF37]" />
                <h3 className="font-display text-lg text-white">Pridať na plochu</h3>
              </div>
              <button onClick={() => setShowHelp(false)} data-testid="install-help-close" className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            {isIos() ? (
              <ol className="space-y-3 text-sm text-white/75">
                <li className="flex items-start gap-2"><span className="text-[#D4AF37] font-bold">1.</span> Stlačte tlačidlo <Share size={14} className="inline text-[#D4AF37]" /> <strong className="text-white">Zdieľať</strong> v lište prehliadača Safari.</li>
                <li className="flex items-start gap-2"><span className="text-[#D4AF37] font-bold">2.</span> Vyberte <strong className="text-white">„Pridať na plochu"</strong>.</li>
                <li className="flex items-start gap-2"><span className="text-[#D4AF37] font-bold">3.</span> Potvrďte <strong className="text-white">„Pridať"</strong>. Ikona sa otvorí rovno v Riadiacom centre.</li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-white/75">
                <li className="flex items-start gap-2"><span className="text-[#D4AF37] font-bold">1.</span> Otvorte menu prehliadača <MoreVertical size={14} className="inline text-[#D4AF37]" /> (vpravo hore).</li>
                <li className="flex items-start gap-2"><span className="text-[#D4AF37] font-bold">2.</span> Vyberte <strong className="text-white">„Inštalovať aplikáciu"</strong> alebo <strong className="text-white">„Pridať na plochu"</strong>.</li>
                <li className="flex items-start gap-2"><span className="text-[#D4AF37] font-bold">3.</span> Ikona sa otvorí rovno v Riadiacom centre.</li>
              </ol>
            )}
          </div>
        </div>
      )}
    </>
  );
}
